import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    AutocompleteElement,
    CheckboxElement,
    FormContainer,
    TextFieldElement,
    useForm,
    useWatch,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import {
    BaseProductInput,
    BrandInput,
    ProductLineInput,
    ProductSubtypeInput,
    ProductTypeInput,
} from '@barboxx/admin-components';
import { CREATE_PRODUCT_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Product } from '@barboxx/models';
import { FLAVOR_TYPES } from '@barboxx/types';
import { enumToList } from '@barboxx/utils';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateProductDialog = ({ onClose, onSuccess, open }: Props) => {
    const notifications = useNotifications();

    const formContext = useForm<Product.CreateRequest>({
        resolver: yupResolver(CREATE_PRODUCT_SCHEMA) as any,
        defaultValues: {
            isAlcoholic: true,
            flavor: null,
        },
    });

    const mutateCreateProduct = useMutation({
        mutationKey: ['product'],
        mutationFn: (data: Product.CreateRequest) =>
            Api.Products.Products.postProduct(data),
        onSuccess: () => {
            formContext.reset();
        },
    });

    const onSubmit = async (values: Product.CreateRequest) => {
        return mutateCreateProduct
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created ${values.name}!`, {
                    severity: 'success',
                });
                onClose();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating product!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    const values = useWatch(formContext);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">Create Product</DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <BrandInput />
                    <ProductLineInput />
                    <TextFieldElement label="Name" name="name" fullWidth />
                    <TextFieldElement
                        label="Description"
                        name="description"
                        fullWidth
                    />

                    <TextFieldElement
                        label="Volume (ml)"
                        name="volumeMl"
                        fullWidth
                        type="number"
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                max: 10000,
                            },
                            input: {
                                endAdornment: 'ml',
                            },
                        }}
                    />

                    <CheckboxElement label="Is Alcoholic" name="isAlcoholic" />

                    {values.isAlcoholic && (
                        <TextFieldElement
                            label="ABV Percentage"
                            name="abvPercentage"
                            fullWidth
                            type="number"
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    max: 100,
                                },
                                input: {
                                    endAdornment: '%',
                                },
                            }}
                        />
                    )}
                    <ProductTypeInput />
                    {values.productTypeId && <ProductSubtypeInput />}

                    <AutocompleteElement
                        label="Flavor"
                        name="flavor"
                        options={enumToList(FLAVOR_TYPES)}
                    />

                    <BaseProductInput />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
